import { useEffect, useState } from 'react'

import { Button } from '~/elements/Button'
import { Input } from '~/elements/Input'
import { useCurrentWorkspacePlan, useSpendCapMutation } from '~/features/billing/hooks/useBillingHooks'
import { defaultSpendCap, formatUsd, isPaidPlan } from '~/features/billing/utils'
import { cn } from '~/lib/utils'

type Mode = 'default' | 'custom' | 'off'

function Option({
  mode,
  current,
  onSelect,
  title,
  description,
  children
}: {
  mode: Mode
  current: Mode
  onSelect: (mode: Mode) => void
  title: string
  description: string
  children?: React.ReactNode
}) {
  return (
    <label
      className={cn(
        'flex cursor-pointer items-start gap-3 rounded-lg border p-4 transition',
        current === mode ? 'border-accent bg-accent/5' : 'hover:bg-secondary'
      )}
    >
      <input
        checked={current === mode}
        className="mt-1 accent-accent"
        name="spend-cap"
        onChange={() => onSelect(mode)}
        type="radio"
        value={mode}
      />
      <div className="flex flex-1 flex-col gap-2">
        <div>
          <p className="font-medium text-content">{title}</p>
          <p className="text-sm text-content3">{description}</p>
        </div>
        {children}
      </div>
    </label>
  )
}

export function SpendCapCard({ className }: { className?: string }) {
  const { state, plan } = useCurrentWorkspacePlan()
  const { mutate, isPending } = useSpendCapMutation()

  const savedMode: Mode =
    !state ? 'default'
    : state.spendCap.amount == null ? 'off'
    : state.spendCap.isDefault ? 'default'
    : 'custom'

  const [mode, setMode] = useState<Mode>(savedMode)
  const [amount, setAmount] = useState('')

  useEffect(() => {
    setMode(savedMode)
    setAmount(
      state?.spendCap.amount != null && !state.spendCap.isDefault ? String(state.spendCap.amount) : ''
    )
  }, [savedMode, state?.spendCap.amount, state?.spendCap.isDefault])

  if (!state || !isPaidPlan(state.plan)) return null

  const defaultAmount = defaultSpendCap(plan)
  const parsedAmount = Number(amount)
  const amountValid = amount.trim() !== '' && Number.isFinite(parsedAmount) && parsedAmount > 0
  const dirty =
    mode !== savedMode || (mode === 'custom' && amountValid && parsedAmount !== state.spendCap.amount)
  const canSave = dirty && (mode !== 'custom' || amountValid)

  const save = () => {
    if (mode === 'default') mutate({ amount: defaultAmount, default: true })
    else if (mode === 'off') mutate({ amount: null })
    else mutate({ amount: parsedAmount })
  }

  return (
    <section
      className={cn('flex flex-col gap-4 rounded-xl border bg-fill2 p-5', className)}
      id="billing-spend-cap"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h3 className="font-semibold text-content">Spend cap</h3>
          <p className="max-w-2xl text-sm text-content2">
            When this usage window&apos;s spend reaches the cap, writes and AI actions pause until you raise
            it or the next window starts. Reads always keep working.
          </p>
        </div>
        <div className="text-right text-sm">
          <p className="text-content3">Current cap</p>
          <p className={cn('font-mono font-semibold text-content', state.spendCap.reached && 'text-danger')}>
            {state.spendCap.amount == null ? 'No cap' : formatUsd(state.spendCap.amount)}
            {state.spendCap.isDefault && state.spendCap.amount != null ? ' (default)' : ''}
          </p>
          <p className="text-xs text-content3">Spent so far {formatUsd(state.estimate.total)}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <Option
          current={mode}
          description={
            defaultAmount != null ?
              `${plan?.defaultSpendCapMultiplier}× the ${plan?.name} plan fee: ${formatUsd(defaultAmount)}`
            : '2× the plan fee'
          }
          mode="default"
          onSelect={setMode}
          title="Default"
        />
        <Option
          current={mode}
          description="Set your own limit in USD."
          mode="custom"
          onSelect={setMode}
          title="Custom amount"
        >
          {mode === 'custom' && (
            <Input
              aria-label="Spend cap amount"
              min={1}
              onChange={(event) => setAmount(event.target.value)}
              placeholder={defaultAmount != null ? String(defaultAmount) : '100'}
              prefix={<span className="text-content3">$</span>}
              size="small"
              step={1}
              type="number"
              value={amount}
            />
          )}
        </Option>
        <Option
          current={mode}
          description="Never pause for spend. Overage is billed in full."
          mode="off"
          onSelect={setMode}
          title="No cap"
        />
      </div>

      <div className="flex justify-end gap-2">
        {dirty && (
          <Button onClick={() => setMode(savedMode)} size="small" variant="secondary">
            Reset
          </Button>
        )}
        <Button disabled={!canSave} loading={isPending} onClick={save} size="small" variant="primary">
          Save spend cap
        </Button>
      </div>
    </section>
  )
}
